import React, { useEffect, useState } from 'react';
import { BlinkDot } from '~/components/BlinkDot';
import updatesData from '~/data/updates';
import { useRect } from '~/hooks/useRect';

interface BlinkUpdateDataRaw {
    heading: string;
    text: string;
    imageText: string;
    images: { url: string; alt: string }[];
    content?: JSX.Element;
}

type BlinkUpdateData = { id: number } & BlinkUpdateDataRaw;

const BlinkUpdates = () => {
    const updates: BlinkUpdateData[] = updatesData.map((item, i) => {
        return {
            id: i,
            ...item,
        };
    });
    const [distances, setDistances] = useState<{ id: number; distance: number }[]>(
        updates.map((item) => {
            return { id: item.id, distance: 0 };
        }),
    );
    const [focusedUpdate, setFocusedUpdate] = useState(updates.length > 0 ? updates[0] : null);

    const [containerRect, containerRef] = useRect<HTMLDivElement>();

    useEffect(() => {
        const legible = distances.filter((item, i) => (i === 0 ? item.distance >= 0 : item.distance >= 32));
        if (legible.length === 0) {
            setFocusedUpdate(updates[0]);
        } else {
            const focused = updates.find((update) => update.id == legible[0].id);
            setFocusedUpdate(focused ?? updates[0]);
        }
    }, [distances]);

    return (
        <div className="my-16 grid flex-grow grid-cols-1 gap-32 overflow-y-hidden md:my-0 md:grid-cols-2">
            <div className="nomobile">
                {focusedUpdate && (
                    <div>
                        <div className={`grid ${focusedUpdate.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-32`}>
                            {focusedUpdate.content}
                            {focusedUpdate.images.map((image, i) => (
                                <img loading="lazy" key={focusedUpdate.id + i + image.url} src={image.url} alt={image.alt} />
                            ))}
                        </div>
                        <p className="mt-8">{focusedUpdate.imageText}</p>
                    </div>
                )}
            </div>
            <div className="text-blue-alt-900 news-items-wrapper no-scrollbar max-h-full overflow-y-scroll bg-white md:rounded-t-16">
                <div ref={containerRef} className="scrollable-content no-scrollbar">
                    {updates.map((item, i) => (
                        <NewsItem
                            key={item.id}
                            item={item}
                            active={focusedUpdate?.id === item.id}
                            setDistance={(id: number, distance: number) =>
                                setDistances((prev) => {
                                    return prev.filter((item) => item.id !== id).concat({ id, distance });
                                })
                            }
                            containerRect={containerRect}
                            lastChild={i === updates.length - 1}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

interface NewsItemProps {
    item: BlinkUpdateData;
    active: boolean;
    setDistance: (id: number, distance: number) => void;
    containerRect: DOMRect | undefined;
    lastChild: boolean;
}

const NewsItem = ({ item, active, setDistance, containerRect, lastChild }: NewsItemProps) => {
    const [rect, ref] = useRect<HTMLDivElement>();
    useEffect(() => {
        if (rect && containerRect) {
            setDistance(item.id, rect.y - containerRect.y);
        }
    }, [rect, containerRect]);

    const { heading, text, images, content } = item;
    return (
        <div
            className={`mx-16 my-32 flex flex-row ${
                active ? '' : 'news-item-inactive cursor-pointer'
            } items-baseline transition-all duration-500`}
            ref={ref}
            onClick={() => {
                scrollToRef(ref);
            }}
            style={
                lastChild
                    ? {
                          marginBottom: (containerRect?.height ?? 0) - (rect?.height ?? 0) - 40,
                      }
                    : {}
            }>
            <div className="gap nomobile flex flex-col gap-8">
                <BlinkDot backgroundColor={active ? 'green-400' : 'bluegray-200'} />
            </div>
            <div className="w-full px-24 text-left">
                <h3>{heading}</h3>
                <p>{text}</p>
                <div className="onlymobile overflow-hidden">
                    {content}
                    {images.map((image, i) => (
                        <img key={item.id + i + image.url} src={image.url} alt={image.alt} className="mt-8" />
                    ))}
                    <p className="mt-8 text-12">{item.imageText}</p>
                </div>
            </div>
        </div>
    );
};

function scrollToRef(scrollToRef: React.RefObject<HTMLDivElement>) {
    if (scrollToRef.current && scrollToRef.current.parentNode) {
        const parentContainer = scrollToRef.current.parentNode as HTMLElement;
        const childPosition = scrollToRef.current.offsetTop - parentContainer.offsetTop;
        parentContainer.scrollTo({
            top: childPosition - 32,
            behavior: 'smooth',
        });
    }
}

export default BlinkUpdates;
